import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Pin, PinOff } from 'lucide-react';
import { nodeKey, resolveRoute, type LocalRef } from '@nw/contracts';
import { Button, Spinner } from '@nw/ui';
import { useMe, usePreferences, useRegistry, useUpdatePreferences } from './queries.js';
import { toast } from './toast.js';

/** Route guard: a disabled module's pages explain themselves instead of failing. */
export function ModuleGate({ moduleId, name, children }: { moduleId: string; name: string; children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <Spinner className="m-8" />;
  if (!me.data?.enabledModules.includes(moduleId)) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24">
        <h1 className="font-display text-4xl">{name} is turned off</h1>
        <p className="mt-3 text-[var(--nw-quiet)]">Its records are kept safely. Turn it back on in settings to use it again.</p>
        <Button asChild variant="primary" className="mt-6">
          <Link to="/settings" hash="modules">
            Open module settings
          </Link>
        </Button>
      </div>
    );
  }
  return <>{children}</>;
}

/** Browser-side route for an entity, from the registered route template. */
export function useEntityHref() {
  const registry = useRegistry();
  return (ref: LocalRef) => {
    const def = registry.data?.entityTypes.find((t) => t.typeKey === `${ref.moduleId}:${ref.entityType}`);
    return def ? resolveRoute(def.routeTemplate, ref.entityId) : null;
  };
}

export function PinButton({ entityRef, label, className }: { entityRef: LocalRef; label: string; className?: string }) {
  const prefs = usePreferences();
  const update = useUpdatePreferences();
  const key = nodeKey(entityRef);
  const pins = prefs.data?.pinnedRefs ?? [];
  const pinned = pins.some((p) => nodeKey(p.ref) === key);
  const toggle = () => {
    const next = pinned ? pins.filter((p) => nodeKey(p.ref) !== key) : [...pins, { ref: entityRef, label }].slice(-12);
    update.mutate(
      { pinnedRefs: next },
      {
        onSuccess: () => toast.success(pinned ? 'Unpinned' : 'Pinned to your sidebar'),
        onError: () => toast.error('Pins could not be saved. Try again.'),
      },
    );
  };
  return (
    <Button variant="quiet" size="sm" onClick={toggle} disabled={!prefs.data || update.isPending} aria-pressed={pinned} className={className}>
      {pinned ? <PinOff /> : <Pin />}
      {pinned ? 'Unpin' : 'Pin'}
    </Button>
  );
}
